import type { TransactionsQuery, TransactionType, Ym, Ymd } from '@lira/shared';

/** Estado de filtros da tela de Transações. */
export interface TxFilters {
  type: TransactionType | null;
  status: 'effected' | 'pending' | null;
  categoryIds: string[];
  accountIds: string[];
  tagIds: string[];
  /** Intervalo custom; quando nulo usa o mês visualizado. */
  from: Ymd | null;
  to: Ymd | null;
  search: string;
}

export const EMPTY_FILTERS: TxFilters = {
  type: null,
  status: null,
  categoryIds: [],
  accountIds: [],
  tagIds: [],
  from: null,
  to: null,
  search: '',
};

/** Monta o querystring do GET /transactions a partir dos filtros + mês. */
export function buildTransactionsQuery(month: Ym, f: TxFilters): string {
  const p = new URLSearchParams();
  if (f.from && f.to) {
    p.set('from', f.from);
    p.set('to', f.to);
  } else {
    p.set('month', month);
  }
  if (f.type) p.set('type', f.type);
  if (f.status) p.set('status', f.status);
  if (f.categoryIds.length) p.set('categoryIds', f.categoryIds.join(','));
  if (f.tagIds.length) p.set('tagIds', f.tagIds.join(','));
  if (f.accountIds.length) p.set('accountIds', f.accountIds.join(','));
  return p.toString();
}

/** Mantém a busca potencialmente sensível no body HTTP, fora de URLs e logs de acesso. */
export function buildTransactionsSearchBody(month: Ym, f: TxFilters): TransactionsQuery {
  const body = Object.fromEntries(new URLSearchParams(buildTransactionsQuery(month, f))) as TransactionsQuery;
  const search = f.search.trim();
  return search ? { ...body, search } : body;
}

/**
 * Lê da query string o subconjunto "compartilhável" dos filtros
 * (type/status/conta/categoria). Usado para inicializar a tela a partir de
 * deep-links (KPI do dashboard, "Transações" de uma conta, drill-down de
 * Relatórios) e para restaurar o estado ao recarregar/compartilhar a URL.
 */
export function filtersFromSearchParams(sp: URLSearchParams): Partial<TxFilters> {
  const patch: Partial<TxFilters> = {};
  const type = sp.get('type');
  if (type === 'expense' || type === 'income' || type === 'transfer') patch.type = type;
  const status = sp.get('status');
  if (status === 'effected' || status === 'pending') patch.status = status;
  const acc = sp.get('accountId');
  if (acc) patch.accountIds = [acc];
  const cat = sp.get('categoryId');
  if (cat) patch.categoryIds = [cat];
  const tagIds = (sp.get('tagIds') ?? '').split(',').filter(Boolean);
  if (tagIds.length) patch.tagIds = tagIds;
  return patch;
}

/**
 * Serializa o subconjunto "compartilhável" dos filtros na query string (a tela
 * mantém a URL em sincronia → bookmarkable). Só expressa conta/categoria de
 * seleção única; multi-seleção e intervalo custom ficam apenas no estado local
 * (não são expressáveis no contrato/URL). Retorna a string (sem '?').
 */
export function searchFromFilters(f: TxFilters): string {
  const p = new URLSearchParams();
  if (f.type) p.set('type', f.type);
  if (f.status) p.set('status', f.status);
  if (f.accountIds.length === 1) p.set('accountId', f.accountIds[0]!);
  if (f.categoryIds.length === 1) p.set('categoryId', f.categoryIds[0]!);
  if (f.tagIds.length) p.set('tagIds', f.tagIds.join(','));
  return p.toString();
}

/** Quantidade de filtros ativos (para o badge do botão Filtrar). Ignora a busca. */
export function countActiveFilters(f: TxFilters): number {
  let n = 0;
  if (f.type) n++;
  if (f.status) n++;
  if (f.categoryIds.length) n++;
  if (f.accountIds.length) n++;
  if (f.tagIds.length) n++;
  if (f.from && f.to) n++;
  return n;
}
